// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import type {RuntimeConfigSnapshot} from '@app/features/app/state/RuntimeConfigSnapshot';
import {confirmPrivyWallet, type PrivyWalletBinding} from '@app/features/auth/privy/PrivyApi';
import {
	normalizedImportSecret,
	type PrivyImportKind,
	previewImportAddress,
} from '@app/features/auth/privy/PrivyImportSecret';
import {isPrivyWalletClient, ownEmbeddedWallets, recoverableWallet} from '@app/features/auth/privy/PrivyWalletPolicy';
import {Button} from '@app/features/ui/button/Button';
import {Input} from '@app/features/ui/components/form/FormInput';
import {
	type User,
	useCreateWallet,
	useImportSeedPhrase,
	useImportWallet,
	usePrivy,
	useUser,
	useWallets,
	type WalletWithMetadata,
} from '@privy-io/react-auth';
import {useRef, useState} from 'react';

export function PrivyWalletGate({
	snapshot = RuntimeConfig.getSnapshot(),
	token,
	binding,
	onComplete,
}: {
	snapshot?: RuntimeConfigSnapshot;
	token?: string;
	binding?: PrivyWalletBinding;
	onComplete: (binding: PrivyWalletBinding) => void | Promise<void>;
}) {
	const {ready, authenticated, user, getAccessToken} = usePrivy();
	const {wallets} = useWallets();
	const {createWallet} = useCreateWallet();
	const {refreshUser} = useUser();
	const [refreshedUser, setRefreshedUser] = useState<User | null>(null);
	const {importWallet} = useImportWallet();
	const {importSeedPhrase} = useImportSeedPhrase();
	const [busy, setBusy] = useState(false);
	const inFlight = useRef(false);
	const [error, setError] = useState<string | null>(null);
	const [kind, setKind] = useState<PrivyImportKind | null>(null);
	const [secret, setSecret] = useState('');
	const [preview, setPreview] = useState<string | null>(null);
	const existing = ownEmbeddedWallets(wallets);
	const boundAddress = binding?.wallet_address ?? null;
	const recovered = recoverableWallet(wallets, boundAddress);
	const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
	const address = selectedAddress ?? recovered?.address ?? null;
	const walletUser = refreshedUser ?? user;
	const hasLinkedWallet =
		walletUser?.linkedAccounts.some(
			(account) =>
				account.type === 'wallet' && account.chainType === 'ethereum' && isPrivyWalletClient(account.walletClientType),
		) ?? false;
	const available = ready && authenticated && !busy;
	const clear = () => {
		setSecret('');
		setPreview(null);
		setKind(null);
	};
	const refreshKnownWallets = async (): Promise<Array<WalletWithMetadata>> => {
		const refreshed = await refreshUser();
		if (user?.id && refreshed.id !== user.id) throw new Error('Wallet session changed');
		setRefreshedUser(refreshed);
		return refreshed.linkedAccounts.filter(
			(account): account is WalletWithMetadata =>
				account.type === 'wallet' &&
				account.chainType === 'ethereum' &&
				isPrivyWalletClient(account.walletClientType) &&
				account.userCanSign !== false,
		);
	};
	const run = async (action: () => Promise<void>, recoveryAddress?: string) => {
		if (inFlight.current || !available) return;
		inFlight.current = true;
		setBusy(true);
		setError(null);
		try {
			await action();
		} catch {
			clear();
			let recovered = false;
			try {
				const known = await refreshKnownWallets();
				const target = recoveryAddress ?? boundAddress;
				const existing = target
					? known.find((wallet) => wallet.address.toLowerCase() === target.toLowerCase())
					: known.length === 1
						? known[0]
						: null;
				if (existing) {
					setSelectedAddress(existing.address);
					recovered = true;
				}
			} catch {
				/* A failed refresh never authorizes another create/import. */
			}
			setError(recovered ? '已找到账号已有钱包，请核对地址后继续。' : '钱包操作未完成，请重试。原绑定地址保持不变。');
		} finally {
			inFlight.current = false;
			setBusy(false);
		}
	};
	const confirm = () =>
		run(async () => {
			if (!address || (boundAddress && address.toLowerCase() !== boundAddress.toLowerCase()))
				throw new Error('Wallet mismatch');
			const accessToken = await getAccessToken();
			if (!accessToken) throw new Error('Authentication required');
			const confirmed = await confirmPrivyWallet(snapshot, accessToken, address, token);
			if (!confirmed.linked || confirmed.wallet_address?.toLowerCase() !== address.toLowerCase())
				throw new Error('Wallet mismatch');
			clear();
			await onComplete(confirmed);
		});
	const validateImport = () => {
		if (!kind) return;
		try {
			const next = previewImportAddress(kind, secret);
			if (boundAddress && next.toLowerCase() !== boundAddress.toLowerCase()) {
				throw new Error('导入地址与账号原绑定地址不同。请使用原钱包私钥或助记词。');
			}
			setPreview(next);
			setError(null);
		} catch (failure) {
			clear();
			setError(failure instanceof Error ? failure.message : '输入无效。');
		}
	};
	const importSelected = () =>
		run(async () => {
			if (!kind || !preview) return;
			let value = normalizedImportSecret(kind, secret);
			const expected = preview;
			const importKind = kind;
			clear();
			try {
				const known = await refreshKnownWallets();
				const alreadyKnown = known.find((wallet) => wallet.address.toLowerCase() === expected.toLowerCase());
				if (alreadyKnown) {
					setSelectedAddress(alreadyKnown.address);
					return;
				}
				if (known.some((wallet) => wallet.imported)) throw new Error('Another imported wallet already exists');
				const alreadyImported = existing.find((wallet) => wallet.address.toLowerCase() === expected.toLowerCase());
				if (alreadyImported) {
					await alreadyImported.getEthereumProvider();
					setSelectedAddress(alreadyImported.address);
					return;
				}
				const result =
					importKind === 'private-key'
						? await importWallet({privateKey: value})
						: await importSeedPhrase({mnemonic: value, chainType: 'ethereum'});
				const imported = 'wallet' in result ? result.wallet : result;
				if (imported.address.toLowerCase() !== expected.toLowerCase()) throw new Error('Wallet mismatch');
				setSelectedAddress(imported.address);
			} finally {
				value = '';
			}
		}, preview ?? undefined);
	return (
		<main className="daoi-gate" data-flx="auth.privy.wallet-gate">
			<h1 data-flx="auth.privy.wallet-title">设置你的钱包</h1>
			<p data-flx="auth.privy.wallet-description">一个账号使用一个业务钱包，确认后用于资产、铸造和合伙人资格。</p>
			<p className="daoi-muted" data-flx="auth.privy.wallet-recovery-hint">
				已有资产请恢复原钱包地址。邮箱登录恢复账号；导入原私钥或助记词才能保留原地址的资产与资格。
			</p>
			{!ready ? (
				<p role="status" data-flx="auth.privy.wallet-loading">
					正在初始化…
				</p>
			) : !authenticated ? (
				<p role="alert" data-flx="auth.privy.wallet-auth-required">
					请先重新完成邮箱验证，再恢复钱包。
				</p>
			) : null}
			{boundAddress && <p data-flx="auth.privy.wallet-bound">原绑定地址：{boundAddress}</p>}
			{existing.map((wallet) => (
				<Button
					key={wallet.address}
					aria-label={`恢复已有钱包 ${wallet.address}`}
					variant="secondary"
					fitContainer
					disabled={!available || Boolean(boundAddress && wallet.address.toLowerCase() !== boundAddress.toLowerCase())}
					onClick={() =>
						void run(async () => {
							await wallet.getEthereumProvider();
							setSelectedAddress(wallet.address);
						})
					}
					data-flx="auth.privy.wallet-recover"
				>
					恢复已有钱包 {wallet.address.slice(0, 6)}…{wallet.address.slice(-4)}
				</Button>
			))}
			{!boundAddress && !address && existing.length === 0 && !hasLinkedWallet && (
				<Button
					fitContainer
					disabled={!available}
					submitting={busy}
					onClick={() =>
						void run(async () => {
							const known = await refreshKnownWallets();
							if (known.length > 0) {
								if (known.length === 1) setSelectedAddress(known[0].address);
								else setError('账号已有多个钱包，请等待恢复并选择原地址。');
								return;
							}
							const created = await createWallet({createAdditional: false});
							setSelectedAddress(created.address);
						})
					}
					data-flx="auth.privy.wallet-create"
				>
					创建新钱包
				</Button>
			)}
			{hasLinkedWallet && existing.length === 0 && (
				<p role="status" data-flx="auth.privy.wallet-recover-wait">
					正在恢复账号已有钱包，请稍后重试。
				</p>
			)}
			{address && !kind && (
				<>
					<p data-flx="auth.privy.wallet-selected">确认业务地址：{address}</p>
					<Button
						fitContainer
						disabled={!available}
						submitting={busy}
						onClick={() => void confirm()}
						data-flx="auth.privy.wallet-confirm"
					>
						确认使用此地址并继续
					</Button>
				</>
			)}
			{kind ? (
				<div className="daoi-gate-password" data-flx="auth.privy.wallet-import-form">
					{preview ? (
						<>
							<p data-flx="auth.privy.wallet-preview">本机预览地址：{preview}</p>
							<p className="daoi-muted" data-flx="auth.privy.wallet-preview-hint">
								助记词使用标准首个 EVM 地址 m/44'/60'/0'/0/0。请核对原地址后确认导入。
							</p>
							<Button
								fitContainer
								disabled={!available}
								submitting={busy}
								onClick={() => void importSelected()}
								data-flx="auth.privy.wallet-import-confirm"
							>
								确认地址并导入
							</Button>
						</>
					) : (
						<>
							<Input
								label={kind === 'private-key' ? '原钱包私钥' : '12 或 24 个英文助记词'}
								type="password"
								value={secret}
								autoComplete="off"
								autoCorrect="off"
								autoCapitalize="none"
								spellCheck={false}
								onChange={(event) => setSecret(event.target.value)}
								data-flx="auth.privy.wallet-secret"
							/>
							<Button
								fitContainer
								disabled={!available || !secret.trim()}
								onClick={validateImport}
								data-flx="auth.privy.wallet-preview-action"
							>
								在本机预览地址
							</Button>
						</>
					)}
					<Button
						variant="secondary"
						fitContainer
						disabled={busy}
						onClick={clear}
						data-flx="auth.privy.wallet-import-cancel"
					>
						取消
					</Button>
				</div>
			) : (
				<>
					<Button
						variant="secondary"
						fitContainer
						disabled={!available}
						onClick={() => {
							clear();
							setKind('private-key');
						}}
						data-flx="auth.privy.wallet-import-key"
					>
						导入原私钥
					</Button>
					<Button
						variant="secondary"
						fitContainer
						disabled={!available}
						onClick={() => {
							clear();
							setKind('seed-phrase');
						}}
						data-flx="auth.privy.wallet-import-seed"
					>
						导入原助记词
					</Button>
				</>
			)}
			{kind && (
				<p className="daoi-muted" data-flx="auth.privy.wallet-import-privacy">
					输入仅在本次表单内存中用于本机校验及 Privy 安全导入，不发送到 DAOI 服务器。
				</p>
			)}
			{error && (
				<p role="alert" className="daoi-error" data-flx="auth.privy.wallet-error">
					{error}
				</p>
			)}
		</main>
	);
}
